/** @jsxImportSource @emotion/react */

import Typography from '@mui/material/Typography'
import { FC, useMemo } from 'react'
import { Form } from 'react-final-form'
import Divider from '@mui/material/Divider'
import { findOneVideoCategory_findOneVideoCategory as VideoCategory } from '__generatedTypes__/findOneVideoCategory'
import { SubjectEnumType } from '__generatedTypes__/globalTypes'
import DetailsBlock from 'components/DetailsBlock'
import { CourseName } from 'theme/utils'
import { validateRequired } from 'utils/validation'
import { InputField } from 'components/Input/InputField'
import { Checkbox } from 'components/Checkbox'
import * as commonClasses from '../commonStyles'

export type ChapterCompletionData = Pick<
  VideoCategory,
  | 'chapterCompletionTitle'
  | 'chapterCompletionDescription'
  | 'chapterCompletionBtn'
  | 'chapterCompletionBtnUrl'
  | 'chapterNotCompletionTitle'
  | 'chapterNotCompletionDescription'
  | 'chapterNotCompletionBtn'
  | 'chapterNotCompletionBtnUrl'
> & {
  isNeedCompletedButton: boolean
  isNeedNotCompletedButton: boolean
}

interface IProps {
  videoCategory: VideoCategory
  handleSubmit: (data: ChapterCompletionData) => void
}

const ChapterCompletionForm: FC<IProps> = ({ videoCategory, handleSubmit }) => {
  const initialValues: ChapterCompletionData = useMemo(
    () => ({
      ...videoCategory,
      isNeedCompletedButton: !!videoCategory.chapterCompletionBtnUrl,
      isNeedNotCompletedButton: !!videoCategory.chapterNotCompletionBtnUrl,
    }),
    [videoCategory]
  )

  const handleDeleteClick = () => {
    handleSubmit({
      chapterCompletionTitle: null,
      chapterCompletionDescription: null,
      chapterCompletionBtn: null,
      chapterCompletionBtnUrl: null,
      chapterNotCompletionTitle: null,
      chapterNotCompletionDescription: null,
      chapterNotCompletionBtn: null,
      chapterNotCompletionBtnUrl: null,
      isNeedCompletedButton: false,
      isNeedNotCompletedButton: false,
    })
  }

  return (
    <div css={commonClasses.formContainer}>
      <Typography variant="title3" css={commonClasses.title}>
        Edit Chapter Completion
      </Typography>
      <Form<ChapterCompletionData>
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, values, form }) => (
          <form onSubmit={handleSubmit} css={[commonClasses.contentContainer, commonClasses.formContainerPadding]}>
            <div css={commonClasses.block}>
              <Typography variant="title3" css={commonClasses.subtitle}>
                Completed
              </Typography>
              <InputField
                name="chapterCompletionTitle"
                validate={validateRequired}
                label="Title"
                placeholder="Enter Title"
                fullWidth
              />
              <InputField
                name="chapterCompletionDescription"
                validate={validateRequired}
                label="Description"
                placeholder="Enter Description"
                multiline
                fullWidth
              />
              <Checkbox
                label={`Do you need button?`}
                checked={Boolean(values.isNeedCompletedButton)}
                onChange={(_, value) => form.change('isNeedCompletedButton', value)}
              />
              {!!values.isNeedCompletedButton && (
                <>
                  <InputField
                    name="chapterCompletionBtn"
                    validate={validateRequired}
                    label="Button Name"
                    placeholder="Enter Button Name"
                    fullWidth
                  />
                  <InputField
                    name="chapterCompletionBtnUrl"
                    validate={validateRequired}
                    label="Button URL"
                    placeholder="Enter Button URL"
                    fullWidth
                  />
                </>
              )}
              <Divider />
              <Typography variant="title3" css={commonClasses.subtitle}>
                Not Completed
              </Typography>
              <InputField
                name="chapterNotCompletionTitle"
                validate={validateRequired}
                label="Title"
                placeholder="Enter Title"
                fullWidth
              />
              <InputField
                name="chapterNotCompletionDescription"
                validate={validateRequired}
                label="Description"
                placeholder="Enter Description"
                multiline
                fullWidth
              />
              <Checkbox
                label={`Do you need button?`}
                checked={Boolean(values.isNeedNotCompletedButton)}
                onChange={(_, value) => form.change('isNeedNotCompletedButton', value)}
              />
              {!!values.isNeedNotCompletedButton && (
                <>
                  <InputField
                    name="chapterNotCompletionBtn"
                    validate={validateRequired}
                    label="Button Name"
                    placeholder="Enter Button Name"
                    fullWidth
                  />
                  <InputField
                    name="chapterNotCompletionBtnUrl"
                    validate={validateRequired}
                    label="Button URL"
                    placeholder="Enter Button URL"
                    fullWidth
                  />
                </>
              )}
            </div>
            <DetailsBlock
              itemName="Chapter Completion"
              title="Resource Details"
              tag={
                videoCategory.subject.type === SubjectEnumType.VideosOnly
                  ? CourseName.CARS
                  : (videoCategory.subject.tabName as CourseName)
              }
              tagLabel={videoCategory.subject.tabName}
              createDate={videoCategory.createdAt}
              statusLive={videoCategory.chapterCompletionLive}
              onDeleteClick={handleDeleteClick}
              deleteDisabledReason={
                videoCategory.chapterCompletionLive
                  ? "You can't delete Chapter Completion because Live is enabled"
                  : undefined
              }
            />
          </form>
        )}
      />
    </div>
  )
}

export default ChapterCompletionForm
